import YAML, { isMap, Document } from 'yaml';

/**
 * Removes a screen patch from the YAML patches: section by screen ID.
 * Returns the updated YAML string, or the original if screen not found in patches.
 *
 * This function preserves all comments in the YAML document.
 *
 * @param courseYaml – full course YAML string (draft with patches)
 * @param screenId – the id of the screen to remove from patches
 * @returns updated YAML string with patch removed
 */
export default function revertPatch(
  courseYaml: string,
  screenId: string
): string {
  if (!screenId) {
    throw new Error('revertPatch requires a screenId');
  }

  try {
    // Parse with comment preservation
    const doc: Document.Parsed = YAML.parseDocument(courseYaml);

    // Check if patches section exists
    const patches = doc.get('patches');
    if (!patches || !isMap(patches)) {
      console.warn('No patches section found in YAML');
      return courseYaml; // No patches section, nothing to revert
    }

    // Check if this screenId exists in patches
    if (!patches.has(screenId)) {
      console.warn(`Screen "${screenId}" not found in patches`);
      return courseYaml; // Screen not in patches, nothing to revert
    }

    // Remove the screen entry from patches
    patches.delete(screenId);

    // If patches is now empty, remove the patches key entirely
    if (patches.items.length === 0) {
      doc.delete('patches');
      console.log('Removed empty patches section');
    }

    console.log(`✅ Reverted patch for screen: ${screenId}`);
    return doc.toString();
  } catch (err) {
    console.error('revertPatch failed:', err);
    throw new Error(`Failed to revert patch: ${(err as Error).message}`);
  }
}
